import {
  type ReservationRow,
  reservationRow,
} from "@/pages/ServiceDetail/reservationRows";
import type { SlotIntent, SlotIntentForm } from "@/pages/slotIntent";
import type { ReservationStatus, Service } from "@/types/api";

/** The selector's state: the form, and whether a stored position had to be
 *  dropped because the browser could not hold it exactly. */
export type SourceSlotForm = SlotIntentForm & { unsafeLsn?: boolean };

/** Only a change capture source takes a replication slot setting. */
export function showsSlotSelector(provider: string | undefined): boolean {
  return provider === "postgres_cdc";
}

/**
 * The selector's state for a stored setting. A position that is not exact
 * in the browser is never shown as if it were: the field is left blank, to
 * be entered again, rather than prefilled with a rounded number.
 */
export function slotFormFromIntent(intent?: SlotIntent): SourceSlotForm {
  if (!intent) return { mode: "fresh" };
  if ("takeover" in intent) {
    return {
      mode: "takeover",
      reservation: intent.takeover.reservation,
      expectedOwner: intent.takeover.expected_owner,
    };
  }
  const { reservation, expected_confirmed_flush_lsn: lsn } = intent.adopt;
  const exact = Number.isSafeInteger(lsn) && lsn >= 0;
  return {
    mode: "adopt",
    ...(reservation ? { reservation } : {}),
    lsn: exact ? String(lsn) : "",
    ...(exact ? {} : { unsafeLsn: true }),
  };
}

/**
 * Retained slots this change capture service can take over: the same slot
 * name, read through the same PostgreSQL service, and left with a
 * checkpoint to continue from. The browser can't see the cluster's identity,
 * so the target service stands in for it; the engine checks the cluster
 * itself before the pipeline reads anything.
 */
export function takeoverCandidates(
  statuses: readonly ReservationStatus[],
  service: Service,
): ReservationRow[] {
  return statuses
    .filter(({ reservation }) => {
      return (
        reservation.state === "retained" &&
        reservation.has_checkpoint !== false &&
        reservation.identity.slot === service.slot &&
        reservation.target_service === service.target_service
      );
    })
    .map(reservationRow);
}
